// core/db/table.ts

import { Mutex } from 'async-mutex'
import PQueue from 'p-queue'

import { IAddColumnBody, InsertColumnPosition } from '@makyoapp/makyo-sdk/api/Tables'
import {
  ColumnTypes,
  ColumnUserTableAttributes,
  ITable,
  ITableColumn,
  ITableRow,
  ITableSnapshotSettings,
  TableNature,
  TableSortOrder,
} from '@makyoapp/makyo-sdk/models/Table'
import { ColumnAdapter, fromSdkColumnList } from '@makyoapp/makyo-common'

import { ICell } from '@/core/models/columns'
import { parseStandardColumn } from '@/core/models/columns/parsers/parser'
import { IRow } from '@/core/models/tables/row'
import { ResolvedColumnsInfo } from '@/workers/types'

import { ISingleCellCell } from '../models/columns/single-cell'
import { IDB } from './db'
import { FilterConfig, filterData } from './operations/filter'
import { searchData } from './operations/search'
import { sortData } from './operations/sorting'
import { applyUserFilter } from './operations/user-filter'
import { IDBProvider } from './provider'
import { filterUniqueBy, getDefaultColumnValue } from './utils'
import {
  syncQueryColumnAdd,
  syncQueryColumnDelete,
  syncQueryColumnDuplicate,
  syncSourceColumn,
} from './utils/source-sync'

const pqueue = new PQueue({ concurrency: 1 })
const mutex = new Mutex()

export const ADVANCED_COLUMN_TYPES = [
  ColumnTypes.Connection,
  ColumnTypes.Lookup,
  ColumnTypes.Math,
  ColumnTypes.Template,
  ColumnTypes.SingleCell,
  ColumnTypes.Function,
]

export const DEFAULT_LANGUAGE = 'en'
export const DEFAULT_USER_SPECIFIC = ''
export const DEFAULT_TABLE_PAGINATION = {
  page: 0,
  perPage: 100,
}
export type TablePagination = typeof DEFAULT_TABLE_PAGINATION
export type DataLang = string
export type DataUserSpecific = string
export type DbWorkerData = Record<DataLang, Record<DataUserSpecific, ITableRow[]>>

export interface UserTableColumnAttributes {
  email: ITableColumn | undefined
  name: ITableColumn | undefined
  photo: ITableColumn | undefined
  role: ITableColumn | undefined
  phone: ITableColumn | undefined
}

type Subscriber = () => void

export interface TableFormattedData {
  /**
   * (paginated & filtered) rows result
   */
  rows: IRow[]
  /**
   * filtered rows count - used for pagination
   */
  filteredRowsCount: number
  /**
   * all rows count - used for metadata (something like search results count)
   */
  allRowsCount: number
}

export interface IDBTable {
  id: string
  /**
   * current database client
   */
  dbClient: IDB
  /**
   * is source table of snapshot or query table
   */
  isSource?: boolean
  /**
   * is snapshot table
   */
  isSnapshot?: boolean
  /**
   * is query table
   */
  isQuery?: boolean
  /**
   * is session table
   */
  isSessionTable?: boolean
  /**
   * is user table
   */
  isUserTable?: boolean
  /**
   * The position of the table in the database
   */
  index: number
  /**
   * Current table object
   */
  settings: ITable
  /**
   * Is the table already fetched from dbWorker or client
   */
  isFetched: boolean
  /**
   * Current database tablesMap
   */
  tablesMap?: Map<string, IDBTable>
  /**
   * Current columnsMap
   */
  columnsMap: Map<string, ITableColumn>
  /**
   * @userTable - Current user table columns
   */
  userTableColumns?: UserTableColumnAttributes
  /**
   * @internal setTablesMap for each table to keep the same reference
   */
  setTablesMap(tablesMap: Map<string, IDBTable>): void
  /**
   * @internal setup snapshot or query table
   */
  setupSnapshotQueryTable(): void
  /**
   * @internal dbtable instance subscriber callback
   */
  subscribe(callback: Subscriber): () => void
  /**
   * @internal notifySubscribers
   */
  notifySubscribers(notifyDbClient?: boolean): void
  /**
   * GetSnapshotSettings - `Get the settings of the snapshot table`
   */
  GetSnapshotSettings: () => ITableSnapshotSettings
  /**
   * GetSnapshotSourceTable - `Get the source table of the snapshot table`
   */
  GetSnapshotSourceTable: () => IDBTable | undefined
  /**
   * UpdateSnapshotSettings - `Update the settings of the snapshot table`
   */
  UpdateSnapshotSettings: (settings: ITableSnapshotSettings, timezone?: string) => Promise<void>
  /**
   * UpdateQuerySettings - `Update the settings of the query table`
   */
  UpdateQuerySettings: (
    settings: ITableSnapshotSettings,
    timezone?: string,
    notifySubscribers?: boolean
  ) => Promise<void>
  /**
   * RefreshQuery - `Refresh the query table`
   */
  RefreshQuery: () => Promise<void>
  /**
   * GetDbWorkerRows - `Get the rows from the db-worker cache`
   */
  GetDbWorkerRows: (lang: DataLang, userSpecific: DataUserSpecific) => ITableRow[]
  /**
   * SetDbWorkerData - `Set the rows of the db-worker cache`
   * @dbWorker
   */
  SetDbWorkerData: (
    newRows: ITableRow[],
    allRowsCount: number,
    lang: DataLang,
    userSpecific: DataUserSpecific,
    startIndex: number,
    endIndex: number,
    resolvedColumnsInfo: ResolvedColumnsInfo,
    finished: boolean
  ) => void
  /**
   * SetDbWorkerColumnData - `Set the column data of the db-worker cache`
   * @dbWorker
   */
  SetDbWorkerColumnData: (
    data: any[], // the resolved row value for this column
    lang: DataLang,
    userSpecific: DataUserSpecific,
    resolvedColumnId: string | null,
    resolvedMeta: [string, string] | null,
    startIndex: number,
    endIndex: number,
    isLastChunk: boolean
  ) => void
  /**
   * AddDbWorkerRows - `Add the rows to dbWorkerData`
   */
  AddDbWorkerRows: (newRows: ITableRow[], lang: DataLang, userSpecific: DataUserSpecific) => void
  /**
   * UpdateDbWorkerRows - `Update the rows in dbWorkerData`
   */
  UpdateDbWorkerRows: (rows: ITableRow[], lang: DataLang, userSpecific: DataUserSpecific) => void
  /**
   * DeleteDbWorkerRows - `Delete the rows in dbWorkerData`
   */
  DeleteDbWorkerRows: (rowIds: string[], lang: DataLang, userSpecific: DataUserSpecific) => void
  /**
   * ClearDbWorkerRows - `Clear the rows in dbWorkerData`
   */
  ClearDbWorkerRows: (lang: DataLang, userSpecific: DataUserSpecific) => void
  /**
   * GetColumns - `Returns the column list of current table`
   */
  GetColumns: () => ColumnAdapter[]
  /**
   * Used in single cell parser
   */
  GetCellData: (columnId: string, rowIdx: number, lang: DataLang, userSpecific: DataUserSpecific) => ICell | null
  /**
   * Get Cell Data By Row Id
   */
  GetCellDataByRowId: (columnId: string, rowId: string, lang: DataLang, userSpecific: DataUserSpecific) => ICell | null
  /**
   * Get Row Data by session id
   */
  GetRowDataBySessionId: (
    sessionId: string | null | undefined,
    lang: DataLang,
    userSpecific: DataUserSpecific
  ) => IRow | null
  /**
   * Get Row Data (by row index)
   */
  GetRowData: (rowIdx: number, lang: DataLang, userSpecific: DataUserSpecific, defaultFirstRow?: boolean) => IRow | null
  /**
   * Get Row Data (by row id)
   */
  GetRowDataByRowId: (
    rowId: string,
    lang: DataLang,
    userSpecific: DataUserSpecific,
    defaultFirstRow?: boolean
  ) => IRow | null
  /**
   * Get Row Index By Row Id
   */
  GetRowIndexByRowId: (rowId: string, lang: DataLang, userSpecific: DataUserSpecific) => number
  /**
   * Get Row Indexes
   */
  GetRowIndexesMap: (lang?: DataLang, userSpecific?: DataUserSpecific) => Map<string, number>
  /**
   * FetchInitialData - Fetch initial data from the provider and set as dbWorkerData placeholder
   * @deprecated
   */
  FetchInitialData: (lang: DataLang, userSpecific: DataUserSpecific) => Promise<Uint8Array>
  /**
   * FormatToIRow - `Format ITableRow to IRow`
   */
  FormatToIRow: (rows: ITableRow[]) => IRow[]
  /**
   * FormatToRaw - `Format IRow to ITableRow`
   */
  FormatToRaw: (rows: IRow[]) => ITableRow[]
  /**
   * GetFormattedData - New fn to format data from db-worker
   */
  GetFormattedData: (
    lang: DataLang,
    userSpecific: DataUserSpecific,
    pagination?: TablePagination,
    filter?: FilterConfig,
    sort?: { sortType: string; sortBy: string },
    search?: string,
    userFilter?: Record<string, any>,
    componentId?: string
  ) => Promise<TableFormattedData>
  /**
   * UpdateData
   */
  UpdateData: (data: ITableRow[], lang?: DataLang, userSpecific?: DataUserSpecific) => Promise<void>
  /**
   * DuplicateRows
   */
  DuplicateRows: (rowIds: string[], lang: DataLang, userSpecific: DataUserSpecific) => void
  /**
   * AddRow - Add new (blank) row data
   */
  AddRow: (newRow: ITableRow, lang?: DataLang, userSpecific?: DataUserSpecific) => Promise<ITableRow | undefined>
  /**
   * Delete Rows
   */
  DeleteRows: (rowIds: string[], language?: DataLang) => Promise<void>
  /**
   * ColumnReorder
   */
  ColumnReorder: (newOrder: string[], dbId: string) => void
  /**
   * UpdateColumnSettings - Updates the settings of a specific column in the table.
   */
  UpdateColumnSettings: (
    tableId: string,
    columnId: string,
    body: IAddColumnBody,
    dbId: string
  ) => Promise<ITableColumn | undefined>
  /**
   * AddColumn - Add new column in the table.
   */
  AddColumn: (newColumn: IAddColumnBody, hiddenByDefault?: boolean, fromSyncQuery?: boolean) => Promise<ITableColumn>
  /**
   * InjectColumn - Inject new column in the table.
   */
  InjectColumn: (tableID: string, newColumn: IAddColumnBody, dbId: string) => Promise<ITableColumn>
  /**
   * DeleteColumn - Delete column in the table.
   */
  DeleteColumn: (columnID: string) => Promise<void>
  /**
   * FreezeColumn - Freeze a column in current table
   */
  FreezeColumn: (colID: string, notifySubscribers?: boolean) => void
  /**
   * HideColumn - Hide column in the table.
   */
  HideColumn: (columnId: string, isHidden: boolean, notifySubscribers?: boolean) => void
  /**
   * HideAllColumns - Hide all columns in the table.
   */
  HideAllColumns: (isHidden: boolean) => void
  /**
   * DuplicateColumn - Duplicate column in the table.
   */
  DuplicateColumn(columnID: string, newColumnName: string): Promise<ITableColumn | undefined>
  /**
   * InsertColumn - Insert column into the table.
   */
  InsertColumn(
    tableID: string,
    targetColumnID: string,
    position: InsertColumnPosition,
    newColumnBody: IAddColumnBody,
    dbId: string
  ): Promise<ITableColumn>
  /**
   * PasteColumn - Paste column into the table.
   */
  PasteColumn(
    fromTableID: string,
    fromColumnID: string,
    targetColumnID?: string,
    position?: InsertColumnPosition
  ): Promise<ITableColumn | undefined>
  /**
   * RenameTable - Rename current table.
   */
  RenameTable(tableId: string, newName: string, dbId: string): Promise<null>
  /**
   * UpdateTimezone - Update the timezone of the table
   */
  UpdateTimezone(tz: string): void
  /**
   * ClearRows - Clear all rows in the table
   */
  ClearRows(): Promise<void>
  /**
   * GetUsers - Get all users data. Only available for user table.
   */
  GetTableUsers: () => IRow[] | undefined
  /**
   * AppendRows - Append new rows to the table
   */
  AppendRows: (payload: ITableRow[], lang?: DataLang, userSpecific?: DataUserSpecific) => Promise<void>
  /**
   * @userTable - Get special user table columns
   */
  GetUserTableColumnAttributes: () => UserTableColumnAttributes | undefined
  /**
   * @userTable - Setup user table columns
   */
  SetupUserTableColumns: (customAttributes?: UserTableColumnAttributes) => void
}

//--------------------------------------------------------------------------------------//
//                               IDBTable implementation                                //
//--------------------------------------------------------------------------------------//

export class DBTable implements IDBTable {
  id: string
  dbClient: IDB
  provider: IDBProvider
  index: number
  settings: ITable
  columnsMap: Map<string, ITableColumn>
  tablesMap?: Map<string, IDBTable>
  isSource?: boolean
  isSessionTable?: boolean
  isSnapshot?: boolean
  isQuery?: boolean
  isFetchedByClient: boolean = false // true if the table is fetched by the client

  isUserTable?: boolean
  userTableColumns?: UserTableColumnAttributes

  private subscribers: Set<Subscriber> = new Set()
  private rowIndexesMap: Record<DataLang, Record<DataUserSpecific, Map<string, number>>> = {}
  private dbWorkerData: DbWorkerData = {}

  constructor(
    dbClient: IDB,
    provider: IDBProvider,
    language: string = DEFAULT_LANGUAGE,
    index: number,
    settings: ITable
  ) {
    this.id = settings._id
    this.dbClient = dbClient
    this.provider = provider
    this.index = index
    this.settings = settings
    this.isSnapshot = settings._Nature === TableNature.Snapshot
    this.isQuery = settings._Nature === TableNature.Query
    this.isSessionTable = settings._Nature === TableNature.Session
    this.isUserTable = settings._IsUserTable

    this.dbWorkerData = {
      [language]: {
        [DEFAULT_USER_SPECIFIC]: [],
      },
    }

    this.rowIndexesMap = {
      [language]: {
        [DEFAULT_USER_SPECIFIC]: new Map(),
      },
    }

    this.columnsMap = new Map(
      (settings._Columns || [])
        // add row id column, hidden by default
        .concat([{ _id: '_id', _Name: 'Row ID', _Type: ColumnTypes.RowID, _IsHidden: true } as ITableColumn])
        .map((col) => {
          const key = col._id || `column_${col._id}`
          return [key, col]
        })
    )

    if (!this.isSnapshot && !this.isQuery) {
      this.validateSlugColumn()
    }

    if (this.isUserTable) {
      this.SetupUserTableColumns()
    }
  }

  get isFetched() {
    return this.isFetchedByClient || this.dbClient.fetchedTableIds.has(this.id)
  }

  validateSlugColumn() {
    const dbId = this.dbClient.dbID
    const noSlugColumn = !this.settings._Columns.find((col) => col._Type === ColumnTypes.Slug || col._Name === 'slug')
    const firstTextColumn = this.settings._Columns.find((col) => col._Type === ColumnTypes.Text)

    if (noSlugColumn && !!dbId && !!firstTextColumn) {
      // generate slug column
      this.AddColumn({
        _id: '',
        _Name: 'slug',
        _Type: ColumnTypes.Slug,
        _Settings: {
          slugColumn: firstTextColumn?._id || '',
        },
        _Connection: {
          _LocalColumnID: '',
          _ForeignTableID: '',
          _ForeignColumnID: '',
          _ForeignColumnIDToShow: '',
          _MatchMultiple: false,
        },
        _Lookup: {
          _ForeignColumnIDToShow: '',
          _ConnectionColumnID: '',
          _Standard: false,
        },
      })
    }
  }

  setTablesMap(tablesMap: Map<string, IDBTable>) {
    this.tablesMap = tablesMap
  }

  subscribe(callback: Subscriber): () => void {
    this.subscribers.add(callback)
    return () => {
      this.subscribers.delete(callback)
    }
  }

  notifySubscribers(notifyDbClient: boolean = false): void {
    if (notifyDbClient) {
      this.dbClient.notifySubscribers()
    }

    this.subscribers.forEach((callback) => {
      try {
        callback()
      } catch (error) {
        console.error('Error in table subscriber:', error)
      }
    })
  }

  /**
   * @internal getTableColumns - `Get the columns of the table`
   */
  private getTableColumns() {
    return this.settings._Columns
  }

  /**
   * @internal setTableColumns - `Set the columns of the table`
   */
  setTableColumns(newColumns: ITableColumn[] | ((prev: ITableColumn[]) => ITableColumn[])) {
    // validation: filter unique (in case of ducpliation) & exclude row id (if any)
    this.settings._Columns = filterUniqueBy(
      [...(typeof newColumns === 'function' ? newColumns(this.settings._Columns) : newColumns)],
      '_id'
    )

    let rowIdCol = this.columnsMap.get('_id')
    if (!rowIdCol) {
      rowIdCol = { _id: '_id', _Name: 'Row ID', _Type: ColumnTypes.RowID, _IsHidden: true } as ITableColumn
    }

    // also update ColumnMap in case we want to run the engine processing
    this.columnsMap = new Map(
      this.settings._Columns
        .concat([rowIdCol]) // * add row id column
        .map((col) => [col._id, col])
    )
  }

  /**
   * @internal setDbWorkerRows - `Set the rows of the db-worker cache`
   */
  private setDbWorkerRows(newRows: ITableRow[], lang: DataLang, userSpecific: DataUserSpecific) {
    // caching
    if (!this.dbWorkerData) {
      this.dbWorkerData = {}
    }
    if (!(lang in this.dbWorkerData)) {
      this.dbWorkerData[lang] = {}
    }
    // update the data immediately
    this.dbWorkerData[lang][userSpecific] = newRows

    this.notifySubscribers()
  }

  /**
   * GetColumns - `Get the columns of the table`
   */
  GetColumns() {
    const columns = Array.from([...this.columnsMap.values()]).filter((c) => c._id !== '_id')
    const result = fromSdkColumnList(columns)
    return result
  }

  /**
   * FetchInitialData - Fetch initial data from the provider and set the db-worker cache
   * @deprecated
   */
  async FetchInitialData(lang: DataLang, userSpecific: DataUserSpecific) {
    const tableId = this.settings._id
    return await this.provider.getTableData(tableId, lang, userSpecific)
  }

  /**
   * formatToIRow - Format ITableRow to IRow
   */
  FormatToIRow(rows: ITableRow[]): IRow[] {
    // Cache the columns to iterate over, avoiding repeated `values()` and `Array.from()` calls.
    const tableColumns = Array.from(this.columnsMap.values())

    return rows.map((rowData) => {
      const formattedRow = { _id: rowData._id } as IRow

      // A for...of loop can be more performant than .forEach
      // as it avoids the overhead of a function call for each iteration.
      for (const column of tableColumns) {
        if (column._id === '_id') {
          continue
        }
        // Pass the cached tableColumns array to the parser.
        formattedRow[column._id] = parseStandardColumn(
          column,
          rowData[column._id],
          this.columnsMap,
          rowData,
          this.settings._Tz
        )
      }
      return formattedRow
    })
  }

  /**
   * formatToRaw - Format IRow to ITableRow
   */
  FormatToRaw(rows: IRow[]): ITableRow[] {
    // Cache the columns to iterate over for performance.
    const tableColumns = Array.from(this.columnsMap.values())

    return rows.map((row) => {
      const { _id, ...restColumn } = row
      const formattedData: Record<string, any> = {}

      // Iterate through defined columns to ensure we only process valid data.
      // This is more efficient than Object.entries().reduce().
      for (const column of tableColumns) {
        const colId = column._id

        // Skip the row id column
        if (colId === '_id') {
          continue
        }

        const rowCell = restColumn[colId]

        // If the cell doesn't exist for this column in the row, skip it.
        if (!rowCell) {
          continue
        }

        let data = rowCell.value

        switch (true) {
          case data === '_CELL_LOADING_': {
            data = null
            break
          }
          case column._Type === ColumnTypes.SingleCell: {
            data = (rowCell as ISingleCellCell).value.value
            break
          }
          case column._Type === ColumnTypes.DateTime: {
            if (data === null || data === undefined) {
              data = null // Use null for empty dates to maintain type compatibility
            } else {
              const date = new Date(data as any)
              data = !isNaN(date.getTime()) ? date.toISOString() : null
            }
            break
          }
        }

        formattedData[colId] = data
      }

      return {
        _id,
        ...formattedData,
      } as ITableRow
    })
  }

  // --------------------------------------------------------------------------------------//
  //                             Base Row Methods                                          //
  // --------------------------------------------------------------------------------------//

  /**
   * GetDbWorkerRows - `Get the rows from the db-worker cache`
   */
  GetDbWorkerRows(lang: DataLang = DEFAULT_LANGUAGE, userSpecific: DataUserSpecific = DEFAULT_USER_SPECIFIC) {
    // get current language data
    if (this.dbWorkerData && lang in this.dbWorkerData) {
      const langData = this.dbWorkerData[lang]
      if (langData && userSpecific in langData) {
        return langData[userSpecific]
      }
    } else {
      // fallback to default language
      if (this.dbWorkerData && DEFAULT_LANGUAGE in this.dbWorkerData) {
        const langData = this.dbWorkerData[DEFAULT_LANGUAGE]
        if (langData && userSpecific in langData) {
          return langData[userSpecific]
        }
      }
    }

    return []
  }

  /**
   * AddDbWorkerRows - `Add the rows of the db-worker cache`
   */
  AddDbWorkerRows(newRows: ITableRow[], lang: DataLang, userSpecific: DataUserSpecific) {
    const currentRows = this.GetDbWorkerRows(lang, userSpecific)

    // check sort order in nature settings
    const natureSettings = this.settings._NatureSettings as ITableSnapshotSettings
    const isDescOrder =
      Object.keys(natureSettings).length > 0 &&
      '_Sort' in natureSettings &&
      natureSettings._Sort._Order === TableSortOrder.Desc

    const updatedRows = isDescOrder ? [...newRows, ...currentRows] : [...currentRows, ...newRows]

    this.setDbWorkerRows(updatedRows, lang, userSpecific)
  }

  /**
   * UpdateDbWorkerRows - `Update the rows of the db-worker cache`
   */
  UpdateDbWorkerRows(rows: ITableRow[], lang: DataLang, userSpecific: DataUserSpecific) {
    // Create a Map for O(1) lookup, which is much faster for bulk updates.
    const updateMap = new Map(rows.map((row) => [row._id, row]))

    const updatedRows = this.GetDbWorkerRows(lang, userSpecific).map((row) => {
      const foundRow = updateMap.get(row._id)
      if (foundRow) return { ...row, ...foundRow }
      return row
    })
    this.setDbWorkerRows(updatedRows, lang, userSpecific)
  }

  /**
   * DeleteDbWorkerRows - `Delete the rows of the db-worker cache`
   */
  DeleteDbWorkerRows(rowIds: string[], lang: DataLang, userSpecific: DataUserSpecific) {
    // Create a Set for O(1) lookup, which is much faster for bulk deletes.
    const deleteSet = new Set(rowIds)

    // Filter out the rows that are in the deleteSet.
    const updatedRows = this.GetDbWorkerRows(lang, userSpecific).filter((row) => !deleteSet.has(row._id))
    this.setDbWorkerRows(updatedRows, lang, userSpecific)
  }

  /**
   * ClearDbWorkerRows - `Clear the rows of the db-worker cache`
   */
  ClearDbWorkerRows(lang: DataLang, userSpecific: DataUserSpecific) {
    this.setDbWorkerRows([], lang, userSpecific)
  }

  // --------------------------------------------------------------------------------------//
  //                             Snapshot/Query Table Methods                              //
  // --------------------------------------------------------------------------------------//

  /**
   * setupSnapshotQueryTable - `Setup the snapshot or query table`
   */
  async setupSnapshotQueryTable() {
    const snapshotSettings = this.settings._NatureSettings as ITableSnapshotSettings
    const sourceTableId = snapshotSettings._Source

    const sourceTable = this.dbClient.tablesMap?.get(sourceTableId)
    if (!sourceTable) return

    // mark the sourceTable as source
    sourceTable.isSource = true

    // add snapshot table to snapshotRefMap
    if (sourceTableId) {
      const sourceTablesMap = this.isSnapshot ? this.dbClient.snapshotRefMap : this.dbClient.queryRefMap

      // check if the source table is already in the map
      if (sourceTablesMap.has(sourceTableId)) {
        sourceTablesMap.get(sourceTableId)!.add(this.id)
      } else {
        sourceTablesMap.set(sourceTableId, new Set([this.id]))
      }
    }

    // clone source columns
    let columnIDs = snapshotSettings._Columns as string[]

    // validate if columns are valid, make sure that they are existing in the source table
    // if not, remove them from the snapshot table
    const invalidColumns = columnIDs.filter((col) => !sourceTable.settings._Columns.some((c) => c._id === col))
    if (invalidColumns.length > 0) {
      console.warn('Invalid columns found in snapshot table', invalidColumns)
      columnIDs = columnIDs.filter((col) => !invalidColumns.includes(col))
      this.settings._NatureSettings._Columns = columnIDs
    }

    try {
      const persistedStates = await this.dbClient.indexedDb.loadColumnStates(this.dbClient.dbID || '', this.id)

      const sourceTableColumns = columnIDs
        .map((col) => {
          const sourceColumn = sourceTable.columnsMap.get(col)
          if (!sourceColumn) return null
          return {
            ...sourceColumn,
            _IsHidden: persistedStates[col]?._IsHidden ?? false,
            _IsFrozen: persistedStates[col]?._IsFrozen ?? false,
            _IsSourceColumn: true,
          }
        })
        .filter((col) => col !== null)

      // for query table, inject original (advance) columns created manually
      if (this.isQuery) {
        // For original columns in query table, check if they have persisted states too
        const originalColumns = [...this.settings._Columns].map((col) => {
          return {
            ...col,
            _IsHidden: persistedStates[col._id]?._IsHidden ?? col._IsHidden,
            _IsFrozen: persistedStates[col._id]?._IsFrozen ?? col._IsFrozen,
            _IsSourceColumn: false,
          }
        })

        sourceTableColumns.push(...originalColumns)
      }

      this.setTableColumns(sourceTableColumns)

      //
    } catch (err) {
      // Fallback to default behavior if loading fails
      const tableColumns = sourceTable.settings._Columns.filter((col) => columnIDs.includes(col._id))

      if (this.isQuery) {
        tableColumns.push(...this.settings._Columns)
      }

      this.setTableColumns(tableColumns)
    }
  }

  /**
   * GetSnapshotSettings - `Get the settings of the snapshot table`
   */
  GetSnapshotSettings() {
    return this.settings._NatureSettings as ITableSnapshotSettings
  }

  /**
   * GetSnapshotSourceTable - `Get the source table of the snapshot table`
   */
  GetSnapshotSourceTable() {
    if (!this.isSnapshot && !this.isQuery) return
    const snapshotSettings = this.GetSnapshotSettings()
    const sourceTableId = snapshotSettings._Source
    return this.tablesMap?.get(sourceTableId)
  }

  /**
   * updateSnapCols - `Update the columns of the snapshot table`
   */
  private updateSnapCols(sourceColumns: ITableColumn[], notify: boolean = false) {
    const snapshotColumns = this.settings._NatureSettings._Columns

    const newColumns = sourceColumns.filter((col) => snapshotColumns.includes(col._id))

    // run through all columns, and persist the visible state
    newColumns.forEach((col) => {
      const currentSnapColumn = this.columnsMap.get(col._id)
      if (currentSnapColumn) {
        col._IsHidden = currentSnapColumn._IsHidden
      }
    })

    this.setTableColumns(newColumns)
  }

  /**
   * UpdateSnapshotSettings - `Update the settings of the snapshot table`
   */
  async UpdateSnapshotSettings(payload: ITableSnapshotSettings, timezone?: string) {
    if (timezone) {
      await this.provider.updateTimezone(this.dbClient.dbID!, this.settings._id, timezone)
    }

    let settings = payload
    settings._Process._LastRanAt = new Date()

    this.settings._NatureSettings = settings

    // save in backend
    await this.provider.updateSnapshotTable(this.id, settings)

    // update columns
    const sourceTableId = settings._Source
    const sourceTable = this.tablesMap?.get(sourceTableId)
    if (sourceTable) {
      this.updateSnapCols(sourceTable.settings._Columns)
    }

    // update dbWorker
    this.dbClient.dbWorker.sendAction({
      type: 'update_snapshot_settings',
      payload: { table_id: this.id, language: DEFAULT_LANGUAGE, settings },
    })

    this.notifySubscribers(true)
  }

  /**
   * UpdateQuerySettings - `Update the settings of the query table`
   */
  async UpdateQuerySettings(payload: ITableSnapshotSettings, timezone?: string, notifySubscriber = true) {
    if (timezone) {
      await this.provider.updateTimezone(this.dbClient.dbID!, this.settings._id, timezone)
    }

    let settings = payload
    settings._Process._LastRanAt = new Date()

    this.settings._NatureSettings = settings

    await this.provider.updateQueryTable(this.id, settings)

    if (notifySubscriber) {
      this.notifySubscribers(true)
    }
  }

  /**
   * RefreshQuery - `Refresh the query table`
   */
  async RefreshQuery() {
    this.settings._NatureSettings._Process._LastRanAt = new Date()

    // update timezone
    await this.provider.updateTimezone(this.dbClient.dbID!, this.settings._id, this.settings._Tz)

    // update query settings
    await this.provider.updateQueryTable(this.id, this.settings._NatureSettings as ITableSnapshotSettings)
  }

  // --------------------------------------------------------------------------------------//
  //                               Internal Db-Worker Methods                              //
  // --------------------------------------------------------------------------------------//

  /**
   * @internal resolveColumnInfo - `Resolve the column info`
   * @dbWorker
   */
  private resolveColumnInfo(resolvedColumnsInfo: ResolvedColumnsInfo) {
    // if resolvedColumnsInfo is not empty, we need to update the columnsMap
    if (resolvedColumnsInfo && Object.keys(resolvedColumnsInfo).length > 0) {
      Object.entries(resolvedColumnsInfo).forEach(([colId, [isVertical, tableColTuple, type]]) => {
        let column = this.columnsMap.get(colId)

        if (!column) return

        if (!tableColTuple) {
          this.columnsMap.set(colId, { ...column, _ResolvedType: type })
          return
        }

        const [sourceTableId, sourceColumnId] = tableColTuple

        const sourceTable = this.tablesMap!.get(sourceTableId)
        if (!sourceTable) return

        const sourceColumn = sourceTable.columnsMap.get(sourceColumnId)
        if (!sourceColumn) return

        let returnType = sourceColumn._Type
        let columnSettings = sourceColumn._Settings

        // special case to resolve function column type and settings
        if (sourceColumn._Type === ColumnTypes.Function) {
          returnType = (sourceColumn._Settings.functionOptions as any).functionValue?.returnType || ColumnTypes.Text

          switch (returnType) {
            case ColumnTypes.DateTime:
              columnSettings = sourceColumn._Settings.dtOptions as any
              break
            case ColumnTypes.Number:
              columnSettings = sourceColumn._Settings.numberOptions as any
              break
            default:
              break
          }
        }

        this.columnsMap.set(colId, {
          ...column,
          _ResolvedType: returnType,
          _ResolvedColumnId: sourceColumnId,
          _ResolvedColumnSettings: columnSettings,
          _ResolvedTableId: sourceTableId,
        })
        return
      })
    }
  }

  /**
   * SetDbWorkerData - `Set the rows of the db-worker wasm`
   * @dbWorker
   */
  SetDbWorkerData(
    newRows: ITableRow[],
    allRowsCount: number,
    lang: DataLang,
    userSpecific: DataUserSpecific,
    startIndex: number,
    endIndex: number,
    resolvedColumnsInfo: ResolvedColumnsInfo,
    isFinished: boolean
  ) {
    // if resolvedColumnsInfo is not empty, we need to update the columnsMap
    const shouldResolveColumns = resolvedColumnsInfo && Object.keys(resolvedColumnsInfo).length > 0
    if (shouldResolveColumns) {
      this.resolveColumnInfo(resolvedColumnsInfo)
    }

    // ensure dbWorkerData structure exists
    if (!this.dbWorkerData) {
      this.dbWorkerData = {}
    }
    if (!(lang in this.dbWorkerData)) {
      this.dbWorkerData[lang] = {}
    }
    if (!(userSpecific in this.dbWorkerData[lang])) {
      this.dbWorkerData[lang][userSpecific] = []
    }

    // get current rows to mutate
    const rows = this.dbWorkerData[lang][userSpecific]

    // delete exceeding rows (if any)
    if (rows.length > allRowsCount) {
      rows.splice(allRowsCount, rows.length - allRowsCount)
    }

    //! filter unique rows, sometimes the db-worker will send duplicate rows
    // ! need to investigate why this is happening
    newRows = filterUniqueBy(newRows, '_id')

    // merge the new rows with the old rows
    rows.splice(
      startIndex,
      endIndex - startIndex,
      ...(rows.length > 0
        ? // merge the new rows with the old rows
          newRows.map((newRow, index) => {
            return { ...(rows[startIndex + index] || {}), ...newRow }
          })
        : newRows)
    )

    // schedule the subscriber notification
    if (isFinished) {
      // mark the table as fetched
      this.isFetchedByClient = true

      this.buildRowIndexesMap(lang, userSpecific)

      this.notifySubscribers()
    }
  }

  /**
   * @internal resolveColumnMeta - `Resolve the column meta`
   * @dbWorker
   */
  private resolveColumnMeta(column: ITableColumn, resolvedMeta: [string, string] | null) {
    if (!resolvedMeta) return

    const [sourceTableId, sourceColumnId] = resolvedMeta

    const sourceTable = this.tablesMap?.get(sourceTableId)
    if (!sourceTable) return

    const sourceColumn = sourceTable.columnsMap.get(sourceColumnId)
    if (!sourceColumn) return

    let returnType = sourceColumn._Type
    let columnSettings = sourceColumn._Settings

    // special case to resolve function column type and settings
    if (sourceColumn._Type === ColumnTypes.Function) {
      returnType = (sourceColumn._Settings.functionOptions as any).functionValue?.returnType || ColumnTypes.Text

      switch (returnType) {
        case ColumnTypes.DateTime:
          columnSettings = sourceColumn._Settings.dtOptions as any
          break
        case ColumnTypes.Number:
          columnSettings = sourceColumn._Settings.numberOptions as any
          break
        default:
          break
      }
    }

    column._ResolvedTableId = sourceTableId
    column._ResolvedColumnId = sourceColumnId
    column._ResolvedColumnSettings = columnSettings
    column._ResolvedType = returnType

    this.columnsMap.set(column._id, {
      ...column,
      _ResolvedTableId: sourceTableId,
      _ResolvedColumnId: sourceColumnId,
      _ResolvedColumnSettings: columnSettings,
      _ResolvedType: returnType,
    })
  }

  /**
   * SetDbWorkerColumnData - `Set the column data of the db-worker cache`
   * @dbWorker
   */
  SetDbWorkerColumnData(
    data: any[],
    lang: DataLang,
    userSpecific: DataUserSpecific,
    resolvedColumnId: string | null,
    resolvedMeta: [string, string] | null,
    startIndex: number,
    endIndex: number,
    isLastChunk: boolean
  ) {
    let column = resolvedColumnId ? this.columnsMap.get(resolvedColumnId) : null
    if (!column) return

    // inject resolved column settings and type (only if not already set)
    this.resolveColumnMeta(column, resolvedMeta)

    // ensure dbWorkerData structure exists
    if (!this.dbWorkerData) {
      this.dbWorkerData = {}
    }
    if (!(lang in this.dbWorkerData)) {
      this.dbWorkerData[lang] = {}
    }
    if (!(userSpecific in this.dbWorkerData[lang])) {
      this.dbWorkerData[lang][userSpecific] = []
    }

    // get current rows to mutate
    const rows = this.dbWorkerData[lang][userSpecific]

    // add bounds checking and provide default values for unresolved rows
    for (let i = startIndex; i < endIndex; i++) {
      const dataIndex = i - startIndex

      // ensure the row exists at this index
      if (i >= 0 && i < rows.length && rows[i]) {
        // check if we have resolved data for this row
        if (dataIndex < data.length) {
          // use resolved data from worker
          rows[i][column._id] = data[dataIndex]
        } else {
          // worker couldn't resolve this row - provide default value
          rows[i][column._id] = getDefaultColumnValue(column._Type) as any
        }
      }
      // if row doesn't exist at all, we can't set properties on undefined
    }

    // schedule the subscriber notification
    if (isLastChunk) {
      this.notifySubscribers()
    }
  }

  // --------------------------------------------------------------------------------------//
  //                               Row Methods                                             //
  // --------------------------------------------------------------------------------------//

  /**
   * GetFormattedData - New fn for db-worker to get formatted data from dbTable
   */
  async GetFormattedData(
    lang: DataLang = DEFAULT_LANGUAGE,
    userSpecific: DataUserSpecific = DEFAULT_USER_SPECIFIC,
    pagination: TablePagination = DEFAULT_TABLE_PAGINATION,
    filter?: FilterConfig,
    sort?: { sortType: string; sortBy: string },
    search?: string,
    userFilter?: Record<string, any>,
    componentId?: string
  ): Promise<TableFormattedData> {
    // Create a stable request identifier. Prefer componentId; fallback to tableId.
    const requestId = componentId || this.id

    const dbWorkerData = this.GetDbWorkerRows(lang, userSpecific)

    if (!dbWorkerData || dbWorkerData.length === 0) {
      return { rows: [], filteredRowsCount: 0, allRowsCount: 0 }
    }

    let allRows = [...dbWorkerData]

    // filter
    allRows = await filterData(requestId, filter, allRows, this, lang, userSpecific)

    // all rows count is after filter
    const allRowsCount = allRows.length

    // user filter
    allRows = await applyUserFilter(requestId, allRows, this, userFilter)

    // search
    allRows = await searchData(search, this, allRows)

    // filtered rows count is after filter, user filter, and search
    const filteredRowsCount = allRows.length

    // sort
    if (sort) {
      // let sortType = sort.sortType
      // if (!sort.sortBy && this.getDefaultRowsOrder() !== sortType) {
      //   sortType = sortType === 'asc' ? 'desc' : 'asc'
      // }
      allRows = sortData(allRows, sort.sortType, sort.sortBy)
    }

    // pagination
    if (pagination) {
      const start = pagination.page * pagination.perPage
      const end = start + pagination.perPage
      allRows = allRows.slice(start, end)
    }

    // convert raw data -> row cell values
    const rows = this.FormatToIRow(allRows)

    return {
      rows,
      filteredRowsCount,
      allRowsCount,
    }
  }

  private buildRowIndexesMap(lang = DEFAULT_LANGUAGE, userSpecific = DEFAULT_USER_SPECIFIC) {
    // ensure the nested cache structure exists
    if (!(lang in this.rowIndexesMap)) {
      this.rowIndexesMap[lang] = {}
    }
    if (!(userSpecific in this.rowIndexesMap[lang])) {
      this.rowIndexesMap[lang][userSpecific] = new Map()
    }

    const currentRows = this.GetDbWorkerRows(lang, userSpecific)
    this.rowIndexesMap[lang][userSpecific] = new Map(currentRows.map((row, index) => [row._id, index]))
    return this.rowIndexesMap[lang][userSpecific]
  }

  // ✅ converted to db-worker
  GetRowIndexesMap(lang = DEFAULT_LANGUAGE, userSpecific = DEFAULT_USER_SPECIFIC) {
    // ensure the nested cache structure exists
    if (!(lang in this.rowIndexesMap)) {
      this.rowIndexesMap[lang] = {}
    }
    if (!(userSpecific in this.rowIndexesMap[lang])) {
      this.rowIndexesMap[lang][userSpecific] = new Map()
    }

    const currentRows = this.GetDbWorkerRows(lang, userSpecific)
    const currentMap = this.rowIndexesMap[lang][userSpecific]

    // return existing map
    if (currentMap.size === currentRows.length) {
      return currentMap
    }

    // build new map
    return this.buildRowIndexesMap(lang, userSpecific)
  }

  // ✅ converted to db-worker
  GetRowIndexByRowId(rowId: string, lang: DataLang, userSpecific: DataUserSpecific) {
    // check row indexss map
    const rowIndex = this.GetRowIndexesMap(lang, userSpecific).get(rowId)
    if (rowIndex !== undefined) return rowIndex

    return this.GetDbWorkerRows(lang, userSpecific).findIndex((r) => r._id === rowId)
  }

  GetRowDataBySessionId(sessionId: string | null | undefined, lang: DataLang, userSpecific: DataUserSpecific) {
    if (!sessionId) return null
    const currentRows = this.GetDbWorkerRows(lang, userSpecific)
    const sessionIdColumn = this.GetColumns().find(
      (c) => c.type === ColumnTypes.SessionID || ['session id', 'Session ID', 'session'].includes(c.name.toLowerCase())
    )
    if (!sessionIdColumn) return null
    const sessionRow = currentRows.find((r) => r[sessionIdColumn.id] === sessionId)
    if (!sessionRow) return null
    return this.FormatToIRow([sessionRow])[0]
  }

  // ✅ converted to db-worker
  GetRowData(rowIdx: number, lang: DataLang, userSpecific: DataUserSpecific) {
    const currentRows = this.GetDbWorkerRows(lang, userSpecific)

    let index = Number(rowIdx)
    if (isNaN(index) || index < 0 || index >= currentRows.length) {
      return null
    }
    const rowData = currentRows[index]
    if (!rowData) return null

    // Return the cell directly from the current rows
    return this.FormatToIRow([rowData])[0]
  }

  // ✅ converted to db-worker
  GetRowDataByRowId(rowId: string, lang: DataLang, userSpecific: DataUserSpecific) {
    const rowData = this.GetDbWorkerRows(lang, userSpecific).find((r) => r._id === rowId)
    if (!rowData) return null

    return this.FormatToIRow([rowData])[0]
  }

  // --------------------------------------------------------------------------------------//
  //                               Row Mutation Methods                                    //
  // --------------------------------------------------------------------------------------//

  private syncQueryAndSourceTable(callback: (dbTable: IDBTable) => void) {
    // skip snapshot, already handled by dbWorker
    if (this.isSnapshot) return

    let relatedTableIds = new Set<string>()

    const collectQueryTableIdsFromSource = (sourceTable: IDBTable) => {
      // collect source table Ids
      if (sourceTable.isFetched && sourceTable.id !== this.id) {
        relatedTableIds.add(sourceTable.id)
      }

      const queryTableIds = this.dbClient.queryRefMap.get(sourceTable.id)
      if (queryTableIds && queryTableIds.size > 0) {
        for (const id of queryTableIds) {
          const table = this.dbClient.tablesMap.get(id)
          if (table && table.isFetched && table.id !== this.id) {
            relatedTableIds.add(table.id)
          }
        }
      }
    }

    // handle update source from query
    if (this.isQuery) {
      const sourceTable = this.GetSnapshotSourceTable()
      if (!sourceTable) return
      collectQueryTableIdsFromSource(sourceTable)
    }

    // handle update query tables from source
    collectQueryTableIdsFromSource(this)

    if (relatedTableIds.size === 0) return

    for (const tableId of relatedTableIds) {
      const table = this.dbClient.tablesMap.get(tableId)
      if (!table || !table.isFetched) continue // make sure query table is already fetched
      pqueue.add(() => callback(table))
    }
  }

  // !only for grid import
  async AppendRows(
    payload: ITableRow[],
    language: DataLang = DEFAULT_LANGUAGE,
    userSpecific: DataUserSpecific = DEFAULT_USER_SPECIFIC
  ) {
    const tableId = this.settings._id

    // Submit new rows
    const newRowsResponse = await this.provider.appendRows(tableId, payload, language, userSpecific)

    // optimistic update
    this.AddDbWorkerRows(newRowsResponse, language, userSpecific)

    // fire db-worker event
    pqueue.add(() => {
      this.dbClient.dbWorker.sendAction({
        type: 'add_rows',
        payload: {
          table_id: tableId,
          language,
          rows: newRowsResponse,
        },
      })
    })

    // sync query and source table
    this.syncQueryAndSourceTable((dbTable) => {
      // optimistic update
      dbTable.AddDbWorkerRows(newRowsResponse, language, userSpecific)

      // fire db-worker event
      this.dbClient.dbWorker.sendAction({
        type: 'add_rows',
        payload: {
          table_id: dbTable.settings._id,
          language,
          rows: newRowsResponse,
        },
      })
    })

    //
  }

  // ?for app user table, check if user row already exist (using email) and merge the data
  async AddRowUserTable(
    payload: ITableRow,
    language: DataLang = DEFAULT_LANGUAGE,
    userSpecific: DataUserSpecific = DEFAULT_USER_SPECIFIC
  ) {
    const emailColumn = this.userTableColumns?.email
    if (!emailColumn) return

    // check the email in payload
    const emailValue = payload[emailColumn._id]
    if (!emailValue) return

    // check if email already exist by searching all rows
    const rows = this.GetDbWorkerRows(language, userSpecific)
    const existingUserRowData = rows.find((row) => row[emailColumn._id] === emailValue)

    if (!!existingUserRowData) {
      const updatedUserRow = { ...existingUserRowData, ...payload }
      await this.UpdateData([updatedUserRow], language, userSpecific)
      return updatedUserRow
    }
  }

  // ✅ converted to db-worker
  async AddRow(
    payload: ITableRow,
    language: DataLang = DEFAULT_LANGUAGE,
    userSpecific: DataUserSpecific = DEFAULT_USER_SPECIFIC
  ) {
    return mutex.runExclusive(async () => {
      // handle snapshot table
      if (this.isSnapshot) {
        const sourceTable = this.GetSnapshotSourceTable()
        if (sourceTable) {
          return sourceTable.AddRow(payload, language, userSpecific)
        }
      }

      let existingUserRow: ITableRow | undefined

      // for app user table, check if user row already exist (using email) and merge the data
      if (this.id === window.appUserTableID) {
        existingUserRow = await this.AddRowUserTable(payload, language, userSpecific)
      }
      if (!!existingUserRow) return existingUserRow

      // for query table
      const sourceTable = this.GetSnapshotSourceTable()
      const tableId = sourceTable?.id || this.settings._id

      let newRow = await this.provider.addRow(tableId, payload, language, userSpecific)

      const rows = this.GetDbWorkerRows(language, userSpecific)

      // validate if every column exist
      this.columnsMap.forEach((col) => {
        if (typeof newRow[col._id] === 'undefined') {
          switch (col._Type) {
            case ColumnTypes.SingleCell:
              newRow[col._id] = rows[0]?.[col._id] ?? null
              break
            default:
              newRow[col._id] = getDefaultColumnValue(col._Type)
          }
        }
      })

      // optimistic update
      this.AddDbWorkerRows([newRow], language, userSpecific)

      // fire db-worker event
      pqueue.add(() => {
        this.dbClient.dbWorker.sendAction({
          type: 'add_row',
          payload: {
            table_id: this.id,
            language,
            row: newRow,
          },
        })
      })

      // sync query and source table
      this.syncQueryAndSourceTable((dbTable) => {
        // optimistic update
        dbTable.AddDbWorkerRows([newRow], language, userSpecific)

        // fire db-worker event
        this.dbClient.dbWorker.sendAction({
          type: 'add_row',
          payload: {
            table_id: dbTable.id,
            language,
            row: newRow,
          },
        })
      })

      return newRow
    })
  }

  // ✅ converted to db-worker
  async UpdateData(
    rows: ITableRow[],
    lang: DataLang = DEFAULT_LANGUAGE,
    user: DataUserSpecific = DEFAULT_USER_SPECIFIC
  ) {
    return mutex.runExclusive(async () => {
      // handle snapshot table
      if (this.isSnapshot) {
        const sourceTable = this.GetSnapshotSourceTable()
        if (sourceTable) {
          await sourceTable.UpdateData(rows, lang, user)
          return
        }
      }

      // for query table
      const sourceTable = this.GetSnapshotSourceTable()
      const tableId = sourceTable?.id || this.settings._id

      // optimistic update
      this.UpdateDbWorkerRows(rows, lang, user)

      // update backend
      await this.provider.updateRows(tableId, rows, lang, user)

      // update db-worker
      pqueue.add(() => {
        this.dbClient.dbWorker.sendAction({
          type: 'update_rows',
          payload: {
            table_id: this.settings._id,
            language: lang,
            rows,
          },
        })
      })

      // sync query and source table
      this.syncQueryAndSourceTable((dbTable) => {
        let rowsToUpdate: ITableRow[] = []
        let rowsToAdd: ITableRow[] = []

        rows.forEach((row) => {
          const foundRowIndex = dbTable.GetRowIndexByRowId(row._id, lang, user)
          if (foundRowIndex === -1) {
            rowsToAdd.push(row)
          } else {
            rowsToUpdate.push(row)
          }
        })

        if (rowsToUpdate.length > 0) {
          // optimistic update
          dbTable.UpdateDbWorkerRows(rows, lang, user)

          // fire db-worker event
          this.dbClient.dbWorker.sendAction({
            type: 'update_rows',
            payload: {
              table_id: dbTable.settings._id,
              language: lang,
              rows,
            },
          })
        }

        if (rowsToAdd.length > 0) {
          // fire db-worker event
          this.dbClient.dbWorker.sendAction({
            type: 'add_rows',
            payload: {
              table_id: dbTable.settings._id,
              language: lang,
              rows: rowsToAdd,
            },
          })
        }
      })
    })
  }

  // ✅ converted to db-worker
  async DuplicateRows(rowIds: string[], language = DEFAULT_LANGUAGE, userSpecific = DEFAULT_USER_SPECIFIC) {
    // handle snapshot table
    if (this.isSnapshot) {
      const sourceTable = this.GetSnapshotSourceTable()
      if (sourceTable) {
        await sourceTable.DuplicateRows(rowIds, language, userSpecific)
        return
      }
    }

    // for query table
    const sourceTable = this.GetSnapshotSourceTable()
    const tableId = sourceTable?.id || this.settings._id

    // call duplicate rows SDK
    const duplicatedRows = await this.provider.duplicateRows(tableId, rowIds, language, userSpecific)

    // clean rows
    const cleanedRows = duplicatedRows.map((row) => {
      let newRow = { ...row }
      for (const colId in newRow) {
        let rowValue = newRow[colId]
        // sometime the rowValue is an object and the language is a key of the object,
        // e.g "colId" : { en: 'row value'}
        // so we need to extract the value of the language key
        if (rowValue !== null && typeof rowValue === 'object' && !!(language in rowValue)) {
          rowValue = rowValue[language as keyof typeof rowValue]
        }
        newRow[colId] = rowValue
      }
      return newRow
    })

    // optimistic update
    this.AddDbWorkerRows(cleanedRows, language, userSpecific)

    this.dbClient.dbWorker.sendAction({
      type: 'add_rows',
      payload: {
        table_id: this.settings._id,
        language,
        rows: cleanedRows,
      },
    })

    // sync query and source table
    this.syncQueryAndSourceTable((dbTable) => {
      // optimistic update
      dbTable.AddDbWorkerRows(cleanedRows, language, userSpecific)

      // fire db-worker event
      this.dbClient.dbWorker.sendAction({
        type: 'add_rows',
        payload: {
          table_id: dbTable.settings._id,
          language: language,
          rows: cleanedRows,
        },
      })
    })
  }

  // ✅ converted to db-worker
  async DeleteRows(rowIds: string[], language = DEFAULT_LANGUAGE) {
    if (this.isSnapshot) {
      const sourceTable = this.GetSnapshotSourceTable()
      if (sourceTable) {
        sourceTable.DeleteRows(rowIds, language)
        return
      }
    }

    // for query table
    const sourceTable = this.GetSnapshotSourceTable()
    const tableId = sourceTable?.id || this.settings._id

    // optimistic update
    this.DeleteDbWorkerRows(rowIds, language, DEFAULT_USER_SPECIFIC)

    // update backend
    await this.provider.deleteRows(tableId, rowIds)

    // update db-worker
    this.dbClient.dbWorker.sendAction({
      type: 'delete_rows',
      payload: {
        table_id: this.settings._id,
        language,
        row_ids: rowIds,
      },
    })

    // sync query and source table
    this.syncQueryAndSourceTable((dbTable) => {
      // optimistic update
      dbTable.DeleteDbWorkerRows(rowIds, language, DEFAULT_USER_SPECIFIC)

      // fire db-worker event
      this.dbClient.dbWorker.sendAction({
        type: 'delete_rows',
        payload: {
          table_id: dbTable.settings._id,
          language: language,
          row_ids: rowIds,
        },
      })
    })
  }

  // ✅ converted to db-worker
  async ClearRows() {
    if (this.isSnapshot || this.isQuery) {
      return
    }

    await this.provider.clearRows(this.id)

    this.ClearDbWorkerRows(DEFAULT_LANGUAGE, DEFAULT_USER_SPECIFIC)

    this.dbClient.dbWorker.sendAction({
      type: 'clear_rows',
      payload: {
        table_id: this.settings._id,
        language: DEFAULT_LANGUAGE,
      },
    })

    // collect related table ids
    let relatedTableIds = new Set<string>()

    const collectRelatedTableIds = () => {
      const queryTableIds = this.dbClient.queryRefMap.get(this.id)
      if (queryTableIds && queryTableIds.size > 0) {
        for (const id of queryTableIds) {
          relatedTableIds.add(id)
        }
      }

      const snapshotTableIds = this.dbClient.snapshotRefMap.get(this.id)
      if (snapshotTableIds && snapshotTableIds.size > 0) {
        for (const id of snapshotTableIds) {
          relatedTableIds.add(id)
        }
      }
    }

    collectRelatedTableIds()

    // clear rows in related tables in batch
    if (relatedTableIds.size > 0) {
      for (const tableId of relatedTableIds) {
        const table = this.dbClient.tablesMap.get(tableId)
        if (!table) continue

        this.dbClient.dbWorker.sendAction({
          type: 'clear_rows',
          payload: {
            table_id: table.settings._id,
            language: DEFAULT_LANGUAGE,
          },
        })

        table.ClearDbWorkerRows(DEFAULT_LANGUAGE, DEFAULT_USER_SPECIFIC)
      }

      // Wait for worker to process all clear_rows actions
      // Using 100ms per related table to ensure all operations complete
      await new Promise((resolve) => setTimeout(resolve, 100 * relatedTableIds.size))
    }
  }

  //--------------------------------------------------------------------------------------//
  //                                      Table Methods                                   //
  //--------------------------------------------------------------------------------------//

  async RenameTable(tableId: string, newName: string, dbId: string): Promise<null | any> {
    const response = await this.provider.renameTable(tableId, newName, dbId)
    this.settings._Name = newName
    this.dbClient.BuildGroupedTables()
    this.dbClient.notifySubscribers()
    this.notifySubscribers(true)
    return response
  }

  async UpdateTimezone(timezone: string): Promise<void> {
    this.provider.updateTimezone(this.dbClient.dbID!, this.settings._id, timezone)
    this.settings._Tz = timezone
    this.notifySubscribers(true)
  }

  // ✅ converted to db-worker
  GetTableUsers() {
    return this.FormatToIRow(this.GetDbWorkerRows(DEFAULT_LANGUAGE, DEFAULT_USER_SPECIFIC))
  }

  // ✅ converted to db-worker
  GetCellData(columnId: string, rowIdx: number, lang: DataLang, userSpecific: DataUserSpecific) {
    // Get the current rows directly without fetching or filtering
    const currentRows = this.GetDbWorkerRows(lang, userSpecific)

    let index = Number(rowIdx)
    if (isNaN(index) || index < 0 || index >= currentRows.length) {
      return null
    }

    const rowData = currentRows[index]
    if (!rowData) return null

    const column = this.columnsMap.get(columnId)
    if (!column) return null

    // Return the cell directly from the current rows
    const value = parseStandardColumn(column, rowData[columnId], this.columnsMap, rowData, this.settings._Tz)

    return value ?? null
  }

  // ✅ converted to db-worker
  GetCellDataByRowId(columnId: string, rowId: string, lang: DataLang, userSpecific: DataUserSpecific) {
    const column = this.columnsMap.get(columnId)
    if (!column) return null

    const currentRows = this.GetDbWorkerRows(lang, userSpecific)
    const rowIndex = this.GetRowIndexByRowId(rowId, lang, userSpecific)
    if (rowIndex === -1) return null

    const rowData = currentRows[rowIndex]
    if (!rowData) return null

    // Return the cell directly from the current rows
    const value = parseStandardColumn(column, rowData[columnId], this.columnsMap, rowData, this.settings._Tz)
    return value ?? null
  }

  // --------------------------------------------------------------------------------------//
  //                               Column Methods                                          //
  // --------------------------------------------------------------------------------------//

  // ✅ converted to db-worker
  async UpdateColumnSettings(tableId: string, columnId: string, body: IAddColumnBody, dbId: string) {
    let tableColumns = this.getTableColumns()
    let columnIndex = tableColumns.findIndex((c) => c._id === columnId)

    if (columnIndex === -1) return

    if (this.isQuery) {
      await this.provider.updateColumnSettings(tableId, columnId, body, dbId)
      window.dispatchEvent(new CustomEvent('db-client-reload'))
      return
    }

    // update column settings in sdk
    this.provider.updateColumnSettings(tableId, columnId, body, dbId)

    // update column settings in dbTable
    let currentColumn = tableColumns[columnIndex]

    // only update dbWorker if type is changed
    const typeChanged = body._Type !== currentColumn._Type

    // or settings is changed in advanced columns
    const settingsChanged =
      [
        ColumnTypes.Function,
        ColumnTypes.Connection,
        ColumnTypes.Lookup,
        ColumnTypes.Math,
        ColumnTypes.Template,
        ColumnTypes.SingleCell,
      ].includes(currentColumn._Type) && JSON.stringify(currentColumn._Settings) !== JSON.stringify(body._Settings)

    // update column
    currentColumn = { ...currentColumn, ...body }

    this.setTableColumns((prev) => {
      prev[columnIndex] = currentColumn
      return prev
    })

    if (typeChanged || settingsChanged) {
      this.dbClient.dbWorker.sendAction({
        type: 'update_column',
        payload: {
          table_id: tableId,
          language: DEFAULT_LANGUAGE,
          column: currentColumn,
          default_cell_value: getDefaultColumnValue(currentColumn._Type),
        },
      })

      this.dbClient.dbWorker.sendAction({
        type: 'set_tables_infos',
        payload: [this.settings],
      })
    }

    // reload if advanced column settings changed
    if (ADVANCED_COLUMN_TYPES.includes(currentColumn._Type)) {
      window.dispatchEvent(new CustomEvent('db-client-reload'))
      return
    }

    this.notifySubscribers(true)

    return currentColumn
  }

  // ✅ converted to db-worker
  async AddColumn(newColumn: IAddColumnBody, hiddenByDefault = false, fromSyncQuery = false): Promise<ITableColumn> {
    if (this.isQuery) {
      const newAddedColumn = await syncQueryColumnAdd(this, newColumn, hiddenByDefault)
      if (newAddedColumn) return newAddedColumn
    }

    const dbId = this.dbClient.dbID || ''

    let resNewColumn = await this.provider.addColumn(this.id, newColumn, dbId)

    if (hiddenByDefault) {
      this.provider.hideColumn(this.id, resNewColumn._id, dbId, true)
      resNewColumn._IsHidden = true
    }

    const updatedColumns = [...this.getTableColumns(), resNewColumn]

    this.setTableColumns(updatedColumns)

    this.dbClient.dbWorker.sendAction({
      type: 'add_column',
      payload: {
        table_id: this.settings._id,
        language: DEFAULT_LANGUAGE,
        column: resNewColumn,
        default_cell_value: getDefaultColumnValue(resNewColumn._Type),
      },
    })

    this.dbClient.dbWorker.sendAction({
      type: 'set_tables_infos',
      payload: [this.settings],
    })

    // four source table, also remove column from query tables
    const isSource = !!this.dbClient.queryRefMap.get(this.id)
    if (isSource) {
      await syncSourceColumn(this.dbClient, this, resNewColumn, 'add')
    }

    if (!fromSyncQuery && ADVANCED_COLUMN_TYPES.includes(resNewColumn._Type)) {
      window.dispatchEvent(new CustomEvent('db-client-reload'))
      return resNewColumn
    }

    this.notifySubscribers(true)

    return resNewColumn
  }

  // !same as AddColumn but without dbWorker action
  // !only for grid import
  async InjectColumn(tableID: string, newColumn: IAddColumnBody, dbId: string): Promise<ITableColumn> {
    const resNewColumn = await this.provider.addColumn(tableID, newColumn, dbId)

    const updatedColumns = [...this.getTableColumns(), resNewColumn]

    this.setTableColumns(updatedColumns)

    return resNewColumn
  }

  // ✅ converted to db-worker
  async DuplicateColumn(columnID: string, newColumnName: string): Promise<ITableColumn | undefined> {
    const tableID = this.settings._id
    const dbId = this.dbClient.dbID || ''

    if (this.isSnapshot) return

    try {
      if (this.isQuery) {
        const newDuplicateColumn = await syncQueryColumnDuplicate(this, columnID, newColumnName)
        if (newDuplicateColumn) return newDuplicateColumn
      }

      const resDuplicateColumn = await this.provider.duplicateColumn(tableID, columnID, dbId, newColumnName)

      const updatedColumns = [...this.getTableColumns()]
      const originalColumnIndex = updatedColumns.findIndex((col) => col._id === columnID)
      updatedColumns.splice(originalColumnIndex + 1, 0, resDuplicateColumn)

      this.setTableColumns(updatedColumns)

      this.dbClient.dbWorker.sendAction({
        type: 'duplicate_column',
        payload: {
          table_id: tableID,
          language: DEFAULT_LANGUAGE,
          column: resDuplicateColumn,
          position: originalColumnIndex + 1,
          old_column_id: columnID,
        },
      })

      this.dbClient.dbWorker.sendAction({
        type: 'set_tables_infos',
        payload: [this.settings],
      })

      // for four source table, also duplicate column from query tables
      const isSource = !!this.dbClient.queryRefMap.get(this.id)
      if (isSource) {
        await syncSourceColumn(this.dbClient, this, resDuplicateColumn, 'add')
      }

      // reload if advanced column settings changed
      if ([ColumnTypes.SingleCell].includes(resDuplicateColumn._Type)) {
        window.dispatchEvent(new CustomEvent('db-client-reload'))
        return
      }

      //
    } catch (error) {
      return await Promise.reject(error)
    }
  }

  // ✅ converted to db-worker
  async InsertColumn(
    tableID: string,
    targetColumnID: string,
    position: InsertColumnPosition,
    newColumnBody: IAddColumnBody,
    dbId: string
  ) {
    try {
      // Insert the column via API at the specified position
      const newColumnResponse = await this.provider.insertColumn(tableID, targetColumnID, position, newColumnBody, dbId)

      if (this.isQuery) {
        // notify dbClient to force reload
        window.dispatchEvent(new CustomEvent('db-client-reload'))
      }

      // Add the new column to the existing columns
      const updatedColumns = [...this.getTableColumns()]
      const targetIndex = updatedColumns.findIndex((col) => col._id === targetColumnID)

      // Insert the new column at the specified position
      if (position === InsertColumnPosition.Left) {
        updatedColumns.splice(targetIndex, 0, newColumnResponse)
      } else if (position === InsertColumnPosition.Right) {
        updatedColumns.splice(targetIndex + 1, 0, newColumnResponse)
      }

      this.setTableColumns(updatedColumns)

      this.dbClient.dbWorker.sendAction({
        type: 'insert_column',
        payload: {
          table_id: tableID,
          language: DEFAULT_LANGUAGE,
          column: newColumnResponse,
          default_cell_value: getDefaultColumnValue(newColumnBody._Type),
          position: position === InsertColumnPosition.Left ? targetIndex : targetIndex + 1,
        },
      })

      this.dbClient.dbWorker.sendAction({
        type: 'set_tables_infos',
        payload: [this.settings],
      })

      this.notifySubscribers(true)

      return newColumnResponse
      //
    } catch (error) {
      return await Promise.reject(error)
    }
  }

  // ✅ converted to db-worker
  async PasteColumn(
    fromTableID: string,
    fromColumnID: string,
    targetColumnID?: string,
    position?: InsertColumnPosition
  ): Promise<ITableColumn | undefined> {
    if (this.isSnapshot) return

    // grab column from clipboard
    const fromTable = this.tablesMap?.get(fromTableID)
    if (!fromTable) return

    const column = fromTable.columnsMap.get(fromColumnID)
    if (!column) return

    const columnPayload: IAddColumnBody = {
      _id: '',
      _Name: column._Name,
      _Type: column._Type,
      _Settings: column._Settings,
      _Connection: column._Connection,
      _Lookup: column._Lookup,
    }

    // check existing name, add suffix `copy` if exists
    const existingColumn = this.columnsMap.get(column._Name)
    if (existingColumn) {
      columnPayload._Name = `${columnPayload._Name} copy`
    }

    const isInsert = position && targetColumnID

    const newColumnsRespone = isInsert
      ? await this.provider.insertColumn(this.id, targetColumnID, position, columnPayload, this.dbClient.dbID!)
      : await this.provider.addColumn(this.id, columnPayload, this.dbClient.dbID!)

    return newColumnsRespone
  }

  // ✅ converted to db-worker
  async DeleteColumn(columnID: string) {
    if (this.isSnapshot) return

    if (this.isQuery) {
      const shouldReturn = await syncQueryColumnDelete(this, columnID)
      if (shouldReturn) return
    }

    const column = this.columnsMap.get(columnID)
    if (!column) return

    await this.provider.deleteColumn(this.id, columnID, this.dbClient.dbID!)

    let currentColumns = this.getTableColumns()
    let columnIndex = currentColumns.findIndex((c) => c._id === columnID)
    if (columnIndex === -1) return

    currentColumns.splice(columnIndex, 1)
    this.setTableColumns(currentColumns)

    this.dbClient.dbWorker.sendAction({
      type: 'delete_column',
      payload: {
        table_id: this.id,
        language: DEFAULT_LANGUAGE,
        column_id: columnID,
      },
    })

    this.dbClient.dbWorker.sendAction({
      type: 'set_tables_infos',
      payload: [this.settings],
    })

    // four source table, also remove column from query tables
    const isSource = !!this.dbClient.queryRefMap.get(this.id)
    if (isSource) {
      await syncSourceColumn(this.dbClient, this, column, 'delete')
    }

    this.notifySubscribers(true)
  }

  ColumnReorder(newColumnsOrder: string[], dbId: string) {
    let updatedColumns = newColumnsOrder.map((colId) => this.columnsMap.get(colId)!)

    this.setTableColumns(updatedColumns)
    this.notifySubscribers(true)

    if (this.isSnapshot) return

    this.provider.updateColumnOrder(this.settings._id, newColumnsOrder, dbId)
  }

  // Update the FreezeColumn method to persist state
  async FreezeColumn(columnID: string, notifySubscribers?: boolean) {
    let currentColumns = this.getTableColumns()
    let columnIndex = currentColumns.findIndex((c) => c._id === columnID)
    if (columnIndex === -1) return

    let updatedValue = !currentColumns[columnIndex]._IsFrozen
    currentColumns[columnIndex]._IsFrozen = updatedValue

    this.setTableColumns(currentColumns)
    if (notifySubscribers) {
      this.notifySubscribers(true)
    }

    // Persist to IndexedDB for computed tables
    if (this.isSnapshot || this.isQuery) {
      this.dbClient.indexedDb.saveColumnState(this.dbClient.dbID || '', this.id, columnID, { _IsFrozen: updatedValue })
      return
    }

    // For non-computed tables, use the provider
    let colID = currentColumns[columnIndex]._id
    this.provider.freezeColumn(
      this.id,
      colID,
      {
        _IsFrozen: updatedValue,
      },
      this.dbClient.dbID!
    )
  }

  private async syncColumnState(columns: ITableColumn[]) {
    // load current column state from indexedDB
    let columnStates = await this.dbClient.indexedDb.loadColumnStates(this.dbClient.dbID!, this.id)

    // purge non-existing columns from indexedDB
    for (const columnId in columnStates) {
      if (!columns.find((c) => c._id === columnId)) {
        delete columnStates[columnId]
      }
    }

    // update the column state
    for (const column of columns) {
      columnStates[column._id] = { _IsHidden: column._IsHidden, _IsFrozen: column._IsFrozen }
    }

    // save the column state
    this.dbClient.indexedDb.updateColumnStates(this.dbClient.dbID!, this.id, columnStates)
  }

  // toggle column visibility
  async HideColumn(columnId: string, isHidden: boolean, notifySubscribers: boolean = true) {
    const newColumns = this.settings._Columns.map((c) => {
      const existingColumn = this.columnsMap.get(c._id)
      if (!existingColumn) return c
      if (existingColumn._id === columnId) {
        return { ...existingColumn, _IsHidden: isHidden }
      }
      return existingColumn
    })

    if (this.isSnapshot || this.isQuery) {
      await this.syncColumnState(newColumns)
    }

    // only update original column of current table
    const shouldUpdateSdk =
      this.isSnapshot || this.isQuery ? !this.GetSnapshotSettings()._Columns.includes(columnId) : true

    if (shouldUpdateSdk) {
      this.provider.hideColumn(this.id, columnId, this.dbClient.dbID!, isHidden)
    }

    // update the column list
    this.setTableColumns(newColumns)

    if (notifySubscribers) {
      this.notifySubscribers(true)
    }
  }

  // toggle all columns visibility
  async HideAllColumns(isHidden: boolean) {
    if (!this.dbClient.dbID) return

    const newColumns = this.settings._Columns.map((c) => {
      const existingColumn = this.columnsMap.get(c._id)
      if (!existingColumn) return c
      return { ...existingColumn, _IsHidden: isHidden }
    })

    if (this.isSnapshot || this.isQuery) {
      await this.syncColumnState(newColumns)
    }

    // only update original columns of each table,
    // for query/snapshot the ones from snapshot columns are not included
    const originalColumns =
      this.isSnapshot || this.isQuery
        ? newColumns.filter((c) => !this.GetSnapshotSettings()._Columns.includes(c._id))
        : newColumns

    if (originalColumns.length > 0) {
      Promise.all(
        originalColumns.map(async (c) => this.provider.hideColumn(this.id, c._id, this.dbClient.dbID!, c._IsHidden))
      )
    }

    this.setTableColumns(newColumns)
    this.notifySubscribers(true)
  }

  //--------------------------------------------------------------------------------------//
  //                                User Table Methods                                    //
  //--------------------------------------------------------------------------------------//

  SetupUserTableColumns(customAttributes?: UserTableColumnAttributes) {
    // custom attributes will be injected if app builder use custom user table
    if (!!customAttributes) {
      this.userTableColumns = customAttributes
      return
    }

    this.userTableColumns = this.settings._Columns.reduce<UserTableColumnAttributes>(
      (acc, col) => {
        switch (col._UserTableAttr) {
          case ColumnUserTableAttributes.Email:
            acc.email = col
            break
          case ColumnUserTableAttributes.Name:
            acc.name = col
            break
          case ColumnUserTableAttributes.Photo:
            acc.photo = col
            break
          case ColumnUserTableAttributes.Role:
            acc.role = col
            break
          case ColumnUserTableAttributes.Phone:
            acc.phone = col
            break
          default:
            break
        }
        return acc
      },
      {
        email: undefined,
        name: undefined,
        photo: undefined,
        role: undefined,
        phone: undefined,
      } as UserTableColumnAttributes
    )
  }

  GetUserTableColumnAttributes() {
    return this.userTableColumns
  }

  //--------------------------------------------------------------------------------------//
  //                               Misc Methods                                           //
  //--------------------------------------------------------------------------------------//
}
